// 加工指示エントリの保存ペイロード(キー付与・システム列除去・エントリ統合)の組み立てをまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createKakousijiSavePayloadBuilder(deps) {
    const {
        DeepCopy,
        DicVal,
        KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE,
        KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE,
        KAKOUSIJI_SECTION_139_SAVE_FIELD_IDS,
        applyKakousijiCurrencyPriceRuleToRow,
        buildKakousijiSampleNestedRows,
        extractKakousijiMasterCode,
        firstNonEmptyValue,
        getKakousijiEntryCache,
        getKakousijiEntryCacheForSummaryItem,
        getKakousijiRowsByContext,
        isEmpty,
        isKakousijiSampleGroupSummaryItem,
        kakousijiSampleGroupEntryKeys,
        kakousijiSummaryList,
        normalizeKakousijiDetailForSave,
        normalizeKakousijiGroupNo,
        normalizeKakousijiText,
        normalizeNumericPayloadObject,
        readKakousijiRowValueIgnoreCase,
        resolveCurrentOppiCode,
        resolveKakousijiActiveGroupSelection,
        sanitizeEntryData,
        snapshotActiveKakousijiSelectionData,
        snapshotKakousijiSampleGroupDisplayEntry
    } = deps;

    function applyKakousijiSaveKeys(data, ssdNo, oppiCode) {
        const next = isEmpty(data) ? {} : { ...data };
        const normalizedSsdNo = String(ssdNo ?? "").trim();
        const normalizedOppiCode = String(oppiCode ?? "").trim();
        const noFieldPattern = /^(SSD|SSDX|SSDN|SSDM|SSDS|SSDSH|SSDF|SSDFH|SSDZM|SSDZ|SSDQ|SSDH|SSDY|SSDB|SSDD|SSDE|SSDGM|SSDSAS|SSDNY)_NO$/i;

        if (normalizedSsdNo !== "") {
            Object.keys(next).forEach((key) => {
                if (noFieldPattern.test(String(key))) next[key] = normalizedSsdNo;
            });
            next.SSD_NO = normalizedSsdNo;
            next.SSD_SIJINO = normalizedSsdNo;
        }

        if (normalizedOppiCode !== "") {
            Object.keys(next).forEach((key) => {
                if (/_OPPICD$/i.test(String(key))) next[key] = normalizedOppiCode;
            });
            next.SSD_OPPICD = normalizedOppiCode;
        }

        return next;
    }
    function isKakousijiDetailSystemColumn(columnId) {
        const key = String(columnId ?? "").trim();
        if (key === "") return true;
        if (/^(rowid|rowKey|grip)$/i.test(key)) return true;
        if (/_(NO|OPPICD|INVALID|INPCID|UPPCID|CREATEAC|UPDATEAC|CREATEDATE|UPDATE|HYOJIJUN|GYONO|WARNINGFLG)$/i.test(key)) return true;
        return false;
    }
    function hasKakousijiDetailSpecificValue(row) {
        if (isEmpty(row)) return false;
        return Object.keys(row).some((key) => {
            if (isKakousijiDetailSystemColumn(key)) return false;
            const value = DicVal(row, key, "");
            return String(value ?? "").trim() !== "";
        });
    }
    function buildKakousijiSaveData(snapshot, summaryItem, ssdNo) {
        const sourceData = sanitizeEntryData(DicVal(snapshot, "data", {}));
        const fallbackOppiCode = String(DicVal(summaryItem, "oppiCode", resolveCurrentOppiCode())).trim();
        const oppiCode = String(DicVal(sourceData, "SSD_OPPICD", fallbackOppiCode)).trim();
        return applyKakousijiSaveKeys(sourceData, ssdNo, oppiCode);
    }
    function buildKakousijiSection139SaveRow(sourceData) {
        const row = {};
        KAKOUSIJI_SECTION_139_SAVE_FIELD_IDS.forEach((fieldId) => {
            row[fieldId] = DicVal(sourceData, fieldId, '');
        });
        return row;
    }
    function applyKakousijiSection139Detail(detail, sourceData) {
        const next = isEmpty(detail) ? {} : { ...detail };
        next['139'] = [buildKakousijiSection139SaveRow(sourceData)];
        return next;
    }
    function clearKakousijiSnapshotSystemValues(row, oppiCode, kubunValue, type) {
        const copied = isEmpty(row) ? {} : { ...row };
        delete copied.rowid;
        delete copied.rowKey;
        Object.keys(copied).forEach((key) => {
            if (key === 'SSDSPM') return;
            if (/_NO$/i.test(key)) copied[key] = '';
            if (/_OPPICD$/i.test(key)) copied[key] = oppiCode;
            if (/_INVALID$/i.test(key)) copied[key] = '0';
        });
        if (String(type).trim() === '101') copied.SSDX_KBN = kubunValue;
        if (Array.isArray(copied.SSDSPM)) {
            copied.SSDSPM = copied.SSDSPM.map((detail) => clearKakousijiSnapshotSystemValues(detail, oppiCode, kubunValue, type));
        }
        return copied;
    }
    function normalizeKakousijiSampleSaveRows(type, rows, ssdNo, oppiCode) {
        return buildKakousijiSampleNestedRows(type, rows)
            .filter((parent) => hasKakousijiDetailSpecificValue(parent) || (Array.isArray(parent.SSDSPM) && parent.SSDSPM.some((detail) => hasKakousijiDetailSpecificValue(detail))))
            .map((parent) => {
                const nextParent = normalizeNumericPayloadObject(applyKakousijiSaveKeys({ ...parent, SSDSPM: undefined }, ssdNo, oppiCode));
                nextParent.SSDSP_NO = String(ssdNo ?? '').trim();
                nextParent.SSDSPM = (Array.isArray(parent.SSDSPM) ? parent.SSDSPM : [])
                    .filter((detail) => hasKakousijiDetailSpecificValue(detail))
                    .map((detail) => normalizeNumericPayloadObject(applyKakousijiSaveKeys(detail, ssdNo, oppiCode)));
                return nextParent;
            });
    }
    function isSsdqSaveRow(row) {
        if (isEmpty(row) || typeof row !== 'object') return false;
        return Object.keys(row).some((key) => String(key ?? '').trim().toUpperCase().indexOf('SSDQ_') === 0);
    }
    function resolveSelectedSsdzValuesForSsdqSave() {
        const selection = resolveKakousijiActiveGroupSelection(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE);
        const itemCd = normalizeKakousijiText(DicVal(selection, 'itemCd', ''));
        const groupNo = normalizeKakousijiGroupNo(DicVal(selection, 'groupNo', ''));
        const sourceRows = getKakousijiRowsByContext(KAKOUSIJI_FUKUSHIZAI_BASE_PANEL_TYPE, itemCd, groupNo);
        for (let i = 0; i < sourceRows.length; i++) {
            const row = sourceRows[i];
            const sourceItemCd = extractKakousijiMasterCode(readKakousijiRowValueIgnoreCase(row, 'SSDZ_ITMCITEMCD'))
                || normalizeKakousijiText(readKakousijiRowValueIgnoreCase(row, 'SSDZ_ITMCITEMCD'));
            const sourceColorCd = extractKakousijiMasterCode(readKakousijiRowValueIgnoreCase(row, 'SSDZ_COLORCD'))
                || normalizeKakousijiText(readKakousijiRowValueIgnoreCase(row, 'SSDZ_COLORCD'));
            const sourceGroupNo = normalizeKakousijiGroupNo(readKakousijiRowValueIgnoreCase(row, 'SSDZ_FUKUSIZAICOLORGRP'));
            if (sourceItemCd !== '' || sourceColorCd !== '' || sourceGroupNo !== '') {
                return { itemCd: sourceItemCd, colorCd: sourceColorCd, groupNo: sourceGroupNo };
            }
        }
        return { itemCd: '', colorCd: '', groupNo: '' };
    }
    function prepareSsdqRowsForSaveEntries(entries) {
        const sourceValues = resolveSelectedSsdzValuesForSsdqSave();
        const sourceItemCd = normalizeKakousijiText(DicVal(sourceValues, 'itemCd', ''));
        const sourceColorCd = normalizeKakousijiText(DicVal(sourceValues, 'colorCd', ''));
        const sourceGroupNo = normalizeKakousijiGroupNo(DicVal(sourceValues, 'groupNo', ''));
        let needsItem = false;
        let needsGroup = false;
        (Array.isArray(entries) ? entries : []).forEach((entry) => {
            const detail = DicVal(entry, 'detail', {});
            let hyojijun = 1;
            Object.keys(isEmpty(detail) ? {} : detail).forEach((type) => {
                const rows = Array.isArray(detail[type]) ? detail[type] : [];
                rows.forEach((row) => {
                    if (!isSsdqSaveRow(row)) return;
                    row.SSDQ_HYOJIJUN = String(hyojijun);
                    hyojijun += 1;
                    if (sourceItemCd !== '') row.SSDQ_ITMCITEMCD = sourceItemCd;
                    else needsItem = true;
                    if (sourceColorCd !== '') row.SSDQ_COLORCD = sourceColorCd;
                    if (sourceGroupNo !== '') row.SSDQ_GROUPNO = sourceGroupNo;
                    else needsGroup = true;
                });
            });
        });
        if (needsGroup) return { status: false, message: 'グループを設定してください。' };
        if (needsItem) return { status: false, message: 'アイテムが取得できません。' };
        return { status: true, message: '' };
    }
    function buildKakousijiSaveDetail(snapshot, ssdNo, oppiCode) {
        const sourceDetail = normalizeKakousijiDetailForSave(DicVal(snapshot, "detail", {}));
        const next = {};
        Object.keys(isEmpty(sourceDetail) ? {} : sourceDetail).forEach((type) => {
            // 保存直前に通貨ルール（JPYなら単価(外)=0、外貨なら単価=単価(外)×レート）を再適用する。
            const currencyConfig = KAKOUSIJI_DETAIL_CALC_CONFIG_BY_TYPE[normalizeKakousijiText(type)];
            next[type] = (Array.isArray(sourceDetail[type]) ? sourceDetail[type] : [])
                .filter((row) => hasKakousijiDetailSpecificValue(row))
                .map((row) => {
                    const saveRow = applyKakousijiSaveKeys(row, ssdNo, oppiCode);
                    if (!isEmpty(currencyConfig)) applyKakousijiCurrencyPriceRuleToRow(saveRow, currencyConfig);
                    return normalizeNumericPayloadObject(saveRow);
                });
        });
        return next;
    }
    function normalizeKakousijiSaveEntryKey(entry) {
        if (isEmpty(entry)) return "";
        const data = DicVal(entry, "data", {});
        return String(
            DicVal(entry, "code",
                DicVal(entry, "SSD_NO",
                    DicVal(data, "SSD_NO", DicVal(data, "SSD_SIJINO", DicVal(entry, "key", "")))
                )
            )
        ).trim();
    }
    function mergeKakousijiEntryDetail(baseDetail, updateDetail) {
        const next = DeepCopy(isEmpty(baseDetail) ? {} : baseDetail);
        Object.keys(isEmpty(updateDetail) ? {} : updateDetail).forEach((type) => {
            const updateRows = Array.isArray(updateDetail[type]) ? updateDetail[type] : [];
            if (updateRows.length !== 0 || isEmpty(next[type])) {
                next[type] = DeepCopy(updateRows);
            }
        });
        return next;
    }
    function mergeKakousijiSaveEntry(baseEntry, updateEntry) {
        const base = isEmpty(baseEntry) ? {} : baseEntry;
        const update = isEmpty(updateEntry) ? {} : updateEntry;
        const mergedData = {
            ...DeepCopy(DicVal(base, "data", {})),
            ...DeepCopy(DicVal(update, "data", {}))
        };
        const merged = {
            ...DeepCopy(base),
            ...DeepCopy(update),
            data: mergedData,
            detail: mergeKakousijiEntryDetail(DicVal(base, "detail", {}), DicVal(update, "detail", {}))
        };
        const ssdNo = normalizeKakousijiSaveEntryKey(merged);
        const oppiCode = String(DicVal(merged, "SSD_OPPICD", DicVal(mergedData, "SSD_OPPICD", DicVal(base, "oppiCode", DicVal(update, "oppiCode", resolveCurrentOppiCode()))))).trim();
        merged.code = ssdNo;
        merged.SSD_NO = ssdNo;
        merged.SSD_OPPICD = oppiCode;
        merged.data = applyKakousijiSaveKeys(merged.data, ssdNo, oppiCode);
        merged.detail = buildKakousijiSaveDetail(merged, ssdNo, oppiCode);
        return merged;
    }
    function mergeKakousijiSaveEntries(dataEntries, directEntries) {
        const merged = [];
        const indexByKey = {};
        const appendOrMerge = (entry) => {
            if (isEmpty(entry)) return;
            const key = normalizeKakousijiSaveEntryKey(entry);
            if (key === "") return;
            if (indexByKey[key] === undefined) {
                indexByKey[key] = merged.length;
                merged.push(mergeKakousijiSaveEntry({}, entry));
                return;
            }
            merged[indexByKey[key]] = mergeKakousijiSaveEntry(merged[indexByKey[key]], entry);
        };
        (Array.isArray(dataEntries) ? dataEntries : []).forEach(appendOrMerge);
        (Array.isArray(directEntries) ? directEntries : []).forEach(appendOrMerge);
        return merged;
    }
    function buildPendingKakousijiSaveEntries() {
        snapshotActiveKakousijiSelectionData();
        const entries = [];
        const appendSaveEntry = (item, snapshot, keyOverride = '') => {
            if (isEmpty(snapshot)) return;
            const snapshotData = DicVal(snapshot, "data", {});
            const ssdNo = String(firstNonEmptyValue(item, ["code", "SSD_NO"]) || firstNonEmptyValue(snapshotData, ["SSD_NO", "SSD_SIJINO"]) || keyOverride).trim();
            if (ssdNo === '') return;
            const data = buildKakousijiSaveData(snapshot, { ...item, code: ssdNo }, ssdNo);
            const oppiCode = String(DicVal(data, "SSD_OPPICD", DicVal(item, "oppiCode", resolveCurrentOppiCode()))).trim();
            entries.push({
                key: keyOverride === '' ? DicVal(item, "key", "") : keyOverride,
                code: ssdNo,
                SSD_NO: ssdNo,
                SSD_OPPICD: oppiCode,
                data: data,
                detail: buildKakousijiSaveDetail(snapshot, ssdNo, oppiCode)
            });
        };
        (Array.isArray(kakousijiSummaryList) ? kakousijiSummaryList : []).forEach((item) => {
            if (isKakousijiSampleGroupSummaryItem(item)) {
                kakousijiSampleGroupEntryKeys(item).forEach((entryKey, entryIndex) => {
                    appendSaveEntry(item, snapshotKakousijiSampleGroupDisplayEntry(item, entryKey, entryIndex) || getKakousijiEntryCache(entryKey), entryKey);
                });
                return;
            }
            appendSaveEntry(item, getKakousijiEntryCacheForSummaryItem(item));
        });
        return entries;
    }

    return {
        applyKakousijiSaveKeys,
        isKakousijiDetailSystemColumn,
        hasKakousijiDetailSpecificValue,
        buildKakousijiSaveData,
        buildKakousijiSection139SaveRow,
        applyKakousijiSection139Detail,
        clearKakousijiSnapshotSystemValues,
        normalizeKakousijiSampleSaveRows,
        isSsdqSaveRow,
        resolveSelectedSsdzValuesForSsdqSave,
        prepareSsdqRowsForSaveEntries,
        buildKakousijiSaveDetail,
        normalizeKakousijiSaveEntryKey,
        mergeKakousijiEntryDetail,
        mergeKakousijiSaveEntry,
        mergeKakousijiSaveEntries,
        buildPendingKakousijiSaveEntries
    };
}
